import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  ArrowUturnLeftIcon,
} from '@heroicons/react/24/outline';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { docConvertAPI } from '../../api/docConvertApi';
import { translateDepartments } from './departmentLabels';

const hasReturn = (note) => (note.return_count || 0) > 0 || note.returned_by_me || Boolean((note.donusler || '').trim());

const formatDate = (value) => {
  if (!value) return '—';
  const [y, m, d] = String(value).split('-');
  return d ? `${d}.${m}.${y}` : value;
};

const formatMonth = (ym) => {
  const [y, m] = ym.split('-');
  return `${m}.${y}`;
};

function KpiCard({ label, value, accent, icon: Icon }) {
  return (
    <div className="rounded-2xl border border-slate-200/80 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between">
        <span className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</span>
        {Icon && (
          <span className="grid h-9 w-9 place-items-center rounded-xl" style={{ background: `${accent}1a`, color: accent }}>
            <Icon className="h-5 w-5" />
          </span>
        )}
      </div>
      <div className="mt-2 text-3xl font-bold" style={{ color: accent }}>{value}</div>
    </div>
  );
}

export default function MyNotesDashboardPage() {
  const [notes, setNotes] = useState([]);
  const [displayName, setDisplayName] = useState('');
  const [myReturnCount, setMyReturnCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await docConvertAPI.getMyNotes();
        setNotes(resp?.data || []);
        setDisplayName(resp?.display_name || '');
        setMyReturnCount(resp?.my_return_count || 0);
      } catch (err) {
        setError(err.message || 'Notlar yüklenemedi.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const stats = useMemo(() => {
    const assigned = notes.filter((n) => n.assigned_to_me);
    const donmus = assigned.filter(hasReturn);
    const bekleyen = assigned.filter((n) => !hasReturn(n));

    const byMonth = {};
    assigned.forEach((n) => {
      const ym = (n.not_tarihi || '').slice(0, 7);
      if (!ym) return;
      if (!byMonth[ym]) byMonth[ym] = { month: ym, Döndüğüm: 0, Bekleyen: 0 };
      if (hasReturn(n)) byMonth[ym]['Döndüğüm'] += 1;
      else byMonth[ym]['Bekleyen'] += 1;
    });
    const monthData = Object.values(byMonth)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((row) => ({ ...row, label: formatMonth(row.month) }));

    return {
      assigned,
      assignedCount: assigned.length,
      donmusCount: donmus.length,
      bekleyenCount: bekleyen.length,
      pie: [
        { name: 'Döndüğüm', value: donmus.length, color: '#10B981' },
        { name: 'Bekleyen', value: bekleyen.length, color: '#F43F5E' },
      ].filter((s) => s.value > 0),
      monthData,
    };
  }, [notes]);

  if (loading) {
    return (
      <div className="mx-4 mt-4 rounded-2xl border border-slate-200 bg-white p-10 text-center text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
        Notlarınız yükleniyor…
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-4 mt-4 rounded-2xl border border-red-200 bg-red-50 p-5 text-red-700 shadow-sm dark:border-red-900/70 dark:bg-red-950/40 dark:text-red-200">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-5 px-4 py-4">
      <div className="rounded-2xl border border-slate-200/80 bg-gradient-to-r from-indigo-500 to-violet-500 px-6 py-5 text-white shadow-sm">
        <h1 className="text-2xl font-bold">Panom</h1>
        <p className="mt-1 text-sm text-indigo-50">
          {displayName ? `${displayName} · ` : ''}Size atanan notlar ve dönüş durumunuz
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <KpiCard label="Atanan Not" value={stats.assignedCount} accent="#6366F1" icon={ClipboardDocumentListIcon} />
        <KpiCard label="Döndüğüm" value={stats.donmusCount} accent="#10B981" icon={CheckCircleIcon} />
        <KpiCard label="Bekleyen" value={stats.bekleyenCount} accent="#F43F5E" icon={ClockIcon} />
        <KpiCard label="Yaptığım Dönüş" value={myReturnCount} accent="#0EA5E9" icon={ArrowUturnLeftIcon} />
      </div>

      {stats.assignedCount === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300">
          Size atanmış not bulunmuyor.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-100">Dönüş Durumu</h2>
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie data={stats.pie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={(e) => `${e.name}: ${e.value}`}>
                    {stats.pie.map((entry) => (
                      <Cell key={entry.name} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-2xl border border-slate-200/80 bg-white p-5 shadow-sm dark:border-slate-700 dark:bg-slate-900">
              <h2 className="mb-3 text-sm font-semibold text-slate-700 dark:text-slate-100">Aylık Dağılım</h2>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={stats.monthData} margin={{ left: 0, right: 10, top: 8, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="Döndüğüm" stackId="m" fill="#10B981" />
                  <Bar dataKey="Bekleyen" stackId="m" fill="#F43F5E" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
            <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3 dark:border-slate-700">
              <h2 className="text-sm font-semibold text-slate-700 dark:text-slate-100">
                Atanan Notlar ({stats.assignedCount})
              </h2>
              <Link
                to="/internal-audit/doc-convert/notes"
                className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
              >
                Tüm notlar <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
              </Link>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500 dark:bg-slate-800/60 dark:text-slate-300">
                  <tr>
                    <th className="px-3 py-2">Sıra</th>
                    <th className="px-3 py-2">Tarih</th>
                    <th className="px-3 py-2">Başlık</th>
                    <th className="px-3 py-2">Not</th>
                    <th className="px-3 py-2">Departman</th>
                    <th className="px-3 py-2">Durum</th>
                    <th className="px-3 py-2 text-center">Dönüş</th>
                    <th className="px-3 py-2 text-center">Durumum</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {stats.assigned.map((note) => {
                    const donmus = hasReturn(note);
                    return (
                      <tr key={note.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                        <td className="px-3 py-2 font-semibold text-slate-500">{note.sira_no ?? '—'}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-slate-500">{formatDate(note.not_tarihi)}</td>
                        <td className="px-3 py-2 font-medium text-slate-800 dark:text-slate-100">{note.not_basligi || '—'}</td>
                        <td className="max-w-[22rem] px-3 py-2 text-slate-600 dark:text-slate-300">
                          <span className="line-clamp-2 whitespace-pre-wrap">{note.note_content || '—'}</span>
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-300">
                          {translateDepartments(note.ilgili_departman) || '—'}
                        </td>
                        <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{note.durum || '—'}</td>
                        <td className="px-3 py-2 text-center text-slate-500">{note.return_count || 0}</td>
                        <td className="px-3 py-2 text-center">
                          {donmus ? (
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">
                              Döndüm
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                              Bekliyor
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
